import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import moment from 'moment';
import Title from '../../commonComponents/Title';
import ContentContainer from '../../commonComponents/pageLayout/ContentContainer';
import SubHeader from '../../commonComponents/pageLayout/SubHeader';
import TopicBanner from '../../commonComponents/pageLayout/header/TopicBanner';
import { compose } from '../../services/utilityFunctions';
import translate from '../../commonComponents/translate';
import Notifications from '../../services/notifications';
import LoggedUserContext from '../../contextProviders/LoggedUserContext';
import Loader from '../../services/loader';
import { useModal } from '../../services/customHooks';
import WeelodgeModal from './components/WeelodgeModal';
import IsDirectSellingActive from '../../commonComponents/DirectSellingModel';
import CommissionTotals from './components/totals/CommissionTotals';
import SponsorCommissionList from './components/SponsorCommissionList';
import OrganizationDirectSelling from '../../commonComponents/propTypes/OrganizationDirectSelling';
import CryptoCurrencyNetworkProp from '../../commonComponents/propTypes/crytoCurrencyNetworkProp';
import SaleModal from './components/sale/SaleModal';
import CommissionsSalesGraph from './components/graph/CommissionsSalesGraph';
import CommissionsWalletPicker from './components/wallet-picker/CommissionsWalletPicker';
import { WALLET_TYPE } from './proptype/walletProp';
import TransactionsModal from './components/TransactionsModal';
import WithdrawalModal from './components/withdrawalModal/WithdrawalModal';
import ExchangeModal from './components/exchangeModal/ExchangeModal';
import UserProp from '../../commonComponents/propTypes/userProp';
import CommissionCryptoTotals from './components/totals/CommissionCryptoTotals';
import CurrencyProp from '../profile/propTypes/currencyProp';
import WalletCoinsContent from './components/wallet-picker/WalletCoinsContent';
import {
  checkIfIsRecurrenceInterval,
  getStartDateAndEndDateInterval,
  RECURRENCE_INTERVAL_CUSTOM,
} from '../../services/dateUtilityFunctions';
import AsyncDateRange from '../../commonComponents/forms/inputs/AsyncDateRange';
import BasicModal from '../../commonComponents/modal/BasicModal';

const clearedState = {
  show: false,
  error: false,
  teamVA: null,
  VALeft: null,
  VARight: null,
  remainingBinaryVA: null,
};

function CommissionsDashboard({
  t,
  _csrf,
  directSellingModel,
  directSellingSettings,
  walletEnabled,
  currencies,
  cryptoCurrenciesNetwork,
}) {
  const isWeelodgeCommissionModel =
    directSellingModel ===
    OrganizationDirectSelling.DIRECT_SELLING_TYPE.WEELODGE;

  const { salesIntegration, recurrence } = directSellingSettings || {};
  const isRecurrenceWeekly =
    recurrence && recurrence === OrganizationDirectSelling.RECURRENCE.WEEKLY;

  const [weelodgeModal, setWeelodgeModal] = useModal({
    show: false,
    data: null,
  });
  const [descriptionModal, showDescriptionModal] = useModal({
    show: false,
    data: null,
  });

  const [saleModal, setSaleModal] = useModal({ show: false, data: null });

  const [transactionsModal, setTransactionsModel] = useModal({
    show: false,
    data: null,
  });

  const [withdrawalModal, setWithdrawalModel] = useModal({
    show: false,
    data: null,
  });

  const [exchangeModal, setExchangeModel] = useModal({
    show: false,
    data: null,
  });

  const tableId = 'myCommissionsList';

  const [isRecurrenceInterval, setIsRecurrenceInterval] = useState(false);

  // Loader
  const loader = useRef(new Loader('#commissionsDashboard'));
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isLoading]);

  const { loggedUser } = useContext(LoggedUserContext);

  // Responsible to change tabs
  const [filterActive, setFilterActive] = useState('marketplace');
  /*
  const handleTab = useCallback(tab => {
    setFilterActive(tab);
  }, []);
  */

  // Wallet selection
  const [activeWalletIndex, setActiveWalletIndex] = useState(0);
  const [wallets, setWallets] = useState([]);
  const activeWallet = wallets[activeWalletIndex];
  const isCrypto = activeWallet && activeWallet.type === WALLET_TYPE.CRYPTO;
  const isFiat = activeWallet && activeWallet.type === WALLET_TYPE.FIAT;
  const isCoinsWallet =
    activeWallet &&
    (activeWallet.type === WALLET_TYPE.COINS ||
      activeWallet.type === WALLET_TYPE.CHARITY);

  const seeCommissions =
    loggedUser.type === UserProp.TYPES.COLLABORATOR ||
    loggedUser.role === UserProp.ROLES.ADMIN ||
    activeWallet?.type === WALLET_TYPE.CRYPTO;

  const handleSelectWallet = useCallback(index => {
    setActiveWalletIndex(index);
  }, []);

  // Totals
  const [totals, setTotals] = useState({
    userSales: 0,
    userCommission: 0,
    userVA: 0,
    teamVA: 0,
    currency: loggedUser.currency || 'eur',
  });

  // List — initial range in the plan timezone (BRAVON-6325); the helper also
  // resolves the weekly/monthly recurrence internally.
  const [initialStartDate, initialEndDate] = getStartDateAndEndDateInterval(
    directSellingSettings,
  );
  const startOfMonth = initialStartDate.valueOf();
  const startOfWeek = initialStartDate.valueOf();
  const today = initialEndDate.valueOf();

  const [summaryState, setSummaryState] = useState({
    revenue: 0,
    netRevenue: 0,
    revenueTax: 0,
    tax: 0,
    revenueWithVAT: 0,
    show: false,
    error: false,
    teamVA: 0,
    VALeft: 0,
    VARight: 0,
    remainingBinaryVA: 0,
  });

  const initialState = {
    sponsor: loggedUser._id,
    startDate: isRecurrenceWeekly ? startOfWeek : startOfMonth,
    endDate: today,
    saleStatus: null,
    rangeType: RECURRENCE_INTERVAL_CUSTOM,
  };
  const [filters, setFilters] = useState(initialState);

  const handleDateChange = (startDate, endDate, rangeType, directSelling) => {
    setIsRecurrenceInterval(
      checkIfIsRecurrenceInterval(
        directSelling,
        moment(startDate).utc(),
        moment(endDate).utc(),
        rangeType,
      ),
    );

    console.log(
      '*isRecurrenceInterval(directSelling, startDate, endDate)',
      checkIfIsRecurrenceInterval(directSelling, startDate, endDate, rangeType),
    );

    // Update filters with the new startDate and endDate
    setFilters(prevState => ({
      ...prevState,
      startDate,
      endDate,
      rangeType,
    }));
  };
  const handleFilterChange = field => value =>
    setFilters(prevState => ({ ...prevState, [field]: value }));

  /** API REQUESTS */
  const handleTotals = () => {
    setLoading(true);
    const params = {
      sponsor: loggedUser._id,
      startDate: filters.startDate,
      endDate: filters.endDate,
    };

    const url =
      activeWallet?.type === WALLET_TYPE.CRYPTO
        ? `/api/analytics/market-place/crypto/commissions/totals?currency=${activeWallet?.currencyInfo?.code}`
        : '/api/analytics/market-place/commissions/totals';
    axios
      .get(url, {
        params,
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        setTotals(response.data);
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        console.log(err);
        Notifications.showErrorFromRequest(err, 'error_fetchCommissionTotals');
      });
  };

  const handleWallets = () => {
    setLoading(true);
    axios
      .get('/api/analytics/wallet/total/balance', {
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        setWallets(response.data);
        setLoading(false);
      })
      .catch(err => {
        console.log(err);
        setLoading(false);
        Notifications.showErrorFromRequest(err, 'error_fetchCommissionWallets');
      });
  };

  const handleSummary = () => {
    setLoading(true);
    let params = {};
    if (filters?.rangeType === RECURRENCE_INTERVAL_CUSTOM) {
      params = {
        startDate: filters.startDate,
        endDate: filters.endDate,
      };
    }

    const url = isCrypto
      ? `/jobs/analytics/market-place/crypto/commissions/report/${filters.rangeType}?currency=${activeWallet?.currencyInfo?.code}`
      : `/jobs/analytics/market-place/commissions/report/${filters.rangeType}`;

    axios
      .get(url, {
        params,
        headers: {
          'Accept-Version': '1.0.0',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        setSummaryState({ ...response.data, show: true });
        setLoading(false);
      })
      .catch(err => {
        console.log(err);
        setSummaryState({ ...summaryState, ...clearedState, error: true });
      });
  };

  /** EXPORT PDF */
  const navigateToExternalUrl = (url, shouldOpenNewTab = true) => {
    if (shouldOpenNewTab) {
      window.open(url, '_blank');
    } else {
      window.location.href = url;
    }
  };

  const checkDownloadStatus = jobId => {
    const endpoint = isCrypto
      ? `/jobs/marketCommissions/crypto/generateStatus/${jobId}`
      : `/jobs/marketCommissions/generateStatus/${jobId}`;
    axios
      .get(endpoint)
      .then(({ data: response }) => {
        const { progress, finishedOn, failedReason, returnvalue } =
          response.data;
        loader.current.showProgressLoader(
          `${progress.toFixed(0)}% ${t('message.processing.loading')}...`,
        );
        if (finishedOn) {
          setLoading(false);
          if (failedReason) {
            Notifications.showNotificationError(t('error'), failedReason);
          } else {
            Notifications.showNotificationSuccess(
              t('success'),
              t('success_create'),
            );
            navigateToExternalUrl(returnvalue);
          }
        } else {
          // Repeat until import of products is finished
          setTimeout(() => checkDownloadStatus(jobId), 1000);
        }
        return response.data;
      })
      .catch(err => {
        console.log(err);
      });
  };

  const getReportCommissions = () => {
    setLoading(true);

    let url = isCrypto
      ? `/jobs/report/pdf/crypto/generate/${filters?.rangeType}?`
      : `/jobs/report/pdf/generate/${filters?.rangeType}?`;

    url += `user=${filters.sponsor}`;

    if (isCrypto) {
      url += `&currency=${activeWallet?.currencyInfo?.code}`;
    }

    if (filters?.rangeType === RECURRENCE_INTERVAL_CUSTOM) {
      url += filters?.startDate ? `&startDate=${filters.startDate}` : '';
      url += filters?.endDate ? `&endDate=${filters.endDate}` : '';
    }

    const params = {
      _csrf,
    };
    axios
      .post(url, params)
      .then(({ data: response }) => {
        loader.current.show({
          message: `${t('message.processing.loading')}... 0%`,
        });
        setTimeout(() => {
          checkDownloadStatus(response.data.id);
        }, 1000);
      })
      .catch(err => {
        setLoading(false);
        console.log(err);
      });
  };

  useEffect(() => {
    handleWallets();
  }, []);

  useEffect(() => {
    handleTotals();
    if (
      checkIfIsRecurrenceInterval(
        directSellingSettings,
        filters.startDate,
        filters.endDate,
        filters.rangeType,
      )
    ) {
      handleSummary();
    } else {
      setSummaryState({ ...summaryState, ...clearedState });
    }
  }, [filters.startDate, filters.endDate, activeWallet]);

  const handleListInitialization = useCallback(() => {
    const $tableSelector = $(`#${tableId}`);

    $tableSelector.on('click', '.open-modal', function openModal() {
      const commission = JSON.parse($(this).attr('data-commission'));
      if (isWeelodgeCommissionModel && commission?.sale) {
        const metadata = commission?.sale?.metadata || {};
        setWeelodgeModal({
          show: true,
          data: {
            _id: commission?._id,
            metadata,
          },
        });
      }
      if (
        (!isWeelodgeCommissionModel && salesIntegration) ||
        (isWeelodgeCommissionModel && salesIntegration && !commission?.sale)
      ) {
        setSaleModal({
          show: true,
          data: commission,
        });
      }
    });

    $tableSelector.on(
      'click',
      '.open-modal-description',
      function openModalDescription() {
        const commission = JSON.parse($(this).attr('data-commission'));
        showDescriptionModal({ show: true, data: commission });
      },
    );
  }, [isWeelodgeCommissionModel, salesIntegration, tableId]);

  const handleWeelodgeClose = useCallback(() => {
    setWeelodgeModal({ show: false, data: null });
  }, []);

  const handleSaleClose = useCallback(() => {
    setSaleModal({ show: false, data: null });
  }, []);

  const handleTransactionsClose = useCallback(() => {
    setTransactionsModel({ show: false, data: null });
  }, []);

  const handleWithdrawalClose = useCallback(result => {
    if (result) {
      window.location.reload();
    } else {
      setWithdrawalModel({ show: false, data: null });
    }
  }, []);

  const handleExchangeClose = useCallback(result => {
    if (result) {
      window.location.reload();
    } else {
      setExchangeModel({ show: false, data: null });
    }
  }, []);

  return (
    <>
      {descriptionModal.show && (
        <BasicModal
          id="descriptionModal"
          saveMessage={t('close')}
          onClose={() => showDescriptionModal({ show: false, data: null })}
          title={t('description')}
          ignoreCancelBtn
        >
          <p>{descriptionModal?.data?.description}</p>
        </BasicModal>
      )}
      {saleModal.show && (
        <SaleModal commission={saleModal.data} onClose={handleSaleClose} />
      )}
      {weelodgeModal.show && (
        <WeelodgeModal
          commission={weelodgeModal.data}
          onClose={handleWeelodgeClose}
        />
      )}
      {transactionsModal.show && (
        <TransactionsModal
          activeWallet={activeWallet}
          onClose={handleTransactionsClose}
          filters={filters}
        />
      )}
      {withdrawalModal.show && (
        <WithdrawalModal
          currencies={currencies}
          cryptoCurrenciesNetwork={cryptoCurrenciesNetwork}
          activeWallet={activeWallet}
          onClose={handleWithdrawalClose}
        />
      )}
      {exchangeModal.show && activeWallet && (
        <ExchangeModal
          activeWallet={activeWallet}
          wallets={wallets}
          onClose={handleExchangeClose}
        />
      )}
      <Title title={t('commissions_header')}>
        <ContentContainer>
          <SubHeader title={t('commissions_header')} />
          <TopicBanner />
          <div className="m-content" id="commissionsDashboard">
            <div className="full-page-container-with-subheader">
              <CommissionsWalletPicker
                walletEnabled={walletEnabled}
                wallets={wallets}
                activeWalletIndex={activeWalletIndex}
                onSelectWallet={handleSelectWallet}
                onSeeTransactions={() => setTransactionsModel({ show: true })}
                onWithdrawal={() => setWithdrawalModel({ show: true })}
                onExchange={() => setExchangeModel({ show: true })}
              />
              {seeCommissions ? (
                <>
                  {/** TOTALS */}
                  <div className="commissions-section-header">
                    <div className="commissions-section-title">
                      {isFiat || isCrypto
                        ? t(`commissions.${filterActive}Commissions`)
                        : ''}
                    </div>
                    <div className="commissions-date-filter-container">
                      <AsyncDateRange
                        id="filterDate2"
                        startDate={filters.startDate}
                        endDate={filters.endDate}
                        placeholder={t('commissions.filter_date_placeholder')}
                        onChange={({ startDate, endDate, rangeType }) => {
                          handleDateChange(
                            startDate,
                            endDate,
                            rangeType,
                            directSellingSettings,
                          );
                        }}
                        onClear={() => {
                          setFilters(initialState);
                        }}
                        opens="left"
                        directSellingSettings={directSellingSettings}
                      />
                    </div>
                  </div>
                  {isFiat || isCrypto ? (
                    <>
                      {isCrypto ? (
                        <CommissionCryptoTotals
                          totals={totals}
                          activeWallet={activeWallet}
                        />
                      ) : (
                        <CommissionTotals
                          directSellingSettings={directSellingSettings}
                          totals={totals}
                          filters={filters}
                          summary={summaryState}
                        />
                      )}
                      {/* Collaborator & Team Sales & Revenue */}
                      <CommissionsSalesGraph
                        activeWallet={activeWallet}
                        startDate={filters.startDate}
                        endDate={filters.endDate}
                        directSellingSettings={directSellingSettings}
                      />
                      {/* LIST - My Commissions */}
                      <SponsorCommissionList
                        key={
                          activeWallet?.currencyInfo?.code || activeWallet?.type
                        }
                        onCommissionReport={getReportCommissions}
                        isRecurrenceInterval={isRecurrenceInterval}
                        tableId={tableId}
                        onListInitialization={handleListInitialization}
                        filters={filters}
                        directSellingModel={directSellingModel}
                        directSellingSettings={directSellingSettings}
                        summary={summaryState}
                        currency={loggedUser.currency}
                        onFilterChange={handleFilterChange}
                        isWeelodgeCommissionModel={isWeelodgeCommissionModel}
                        activeWallet={activeWallet}
                      />
                    </>
                  ) : null}
                </>
              ) : null}
              {isCoinsWallet ? (
                <WalletCoinsContent
                  filters={{
                    startDate: filters.startDate,
                    endDate: filters.endDate,
                  }}
                />
              ) : null}
            </div>
          </div>
        </ContentContainer>
      </Title>
    </>
  );
}

CommissionsDashboard.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  directSellingModel: PropTypes.string.isRequired,
  directSellingSettings: PropTypes.shape({
    salesIntegration: PropTypes.bool,
    VAIntegration: PropTypes.bool,
    recurrence: PropTypes.string,
  }).isRequired,
  walletEnabled: PropTypes.bool.isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType))
    .isRequired,
  cryptoCurrenciesNetwork: PropTypes.arrayOf(
    PropTypes.shape(CryptoCurrencyNetworkProp.propType),
  ).isRequired,
};

export default compose(translate, IsDirectSellingActive)(CommissionsDashboard);
